import { z } from 'zod';
import { lenientUuid } from '../schema-extensions';
import { LiabilityTargetType } from '../enums/liability-target-type.enum';

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Lenient schema for liability reference - accepts various formats and coerces to standard
 */
const lenientLiabilityReferenceNodeSchema = z.object({
  type: z.nativeEnum(LiabilityTargetType),
  id: lenientUuid(),
}).transform(val => ({
  type: val.type,
  id: val.id
}));

/**
 * Strict schema for liability reference - enforces exact format
 */
const strictLiabilityReferenceNodeSchema = z.object({
  type: z.nativeEnum(LiabilityTargetType),
  id: z.string().min(1),
}).transform(val => ({
  type: val.type,
  id: val.id
}));

// ============================================================================
// INTERFACE
// ============================================================================

export interface LiabilityReferenceNode {
  type: LiabilityTargetType;
  id: string;
}

// ============================================================================
// NAMESPACE WITH METHODS
// ============================================================================

export namespace LiabilityReferenceNode {
  /**
   * Safely deserialize unknown data into LiabilityReferenceNode
   * Uses lenient parsing to handle various input formats
   */
  export function deserialize(data: unknown): LiabilityReferenceNode {
    return lenientLiabilityReferenceNodeSchema.parse(data);
  }

  /**
   * Create a new LiabilityReferenceNode with validation
   * Uses strict parsing to ensure data integrity
   */
  export function create(data: LiabilityReferenceNode): LiabilityReferenceNode {
    return strictLiabilityReferenceNodeSchema.parse(data);
  }

  /**
   * Validate that data conforms to LiabilityReferenceNode structure
   */
  export function validate(data: unknown): data is LiabilityReferenceNode {
    return strictLiabilityReferenceNodeSchema.safeParse(data).success;
  }
}
